import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Container,
  Form,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    sessionStorage.getItem("orderPage")
      ? parseInt(sessionStorage.getItem("orderPage"))
      : 1
  );
  const [ordersPerPage, setOrdersPerPage] = useState(
    sessionStorage.getItem("ordersPerPage") || 10
  );
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    breadType: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, [currentPage, ordersPerPage, filters]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get(`${API_BASE}/api/orders/enhanced`, {
        headers,
        params: {
          page: currentPage,
          per_page: ordersPerPage === "all" ? 9999 : ordersPerPage,
          start: filters.startDate || undefined,
          end: filters.endDate || undefined,
          bread_type: filters.breadType || undefined,
        },
      });

      setOrders(res.data.orders || []);
      setTotalOrders(res.data.pagination?.total || 0);
      sessionStorage.setItem("orderPage", currentPage);
      sessionStorage.setItem("ordersPerPage", ordersPerPage);
    } catch (err) {
      console.error("Failed to fetch orders", err);
    } finally {
      setLoading(false);
    }
  };

  const summarizeItems = (items = []) => (
    <ul style={{ marginBottom: 0 }}>
      {items.map((item, index) => (
        <li key={index}>
          {item.quantity}x {item.bread_type} ({item.size}")
        </li>
      ))}
    </ul>
  );

  const totalPages =
    ordersPerPage === "all" ? 1 : Math.ceil(totalOrders / ordersPerPage);

  const handleView = (order_id) => navigate(`/customer/view/${order_id}`);
  const handleEdit = (order_id) => navigate(`/customer/edit/${order_id}`);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const handleDelete = async (order_id) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(`${API_BASE}/api/orders/${order_id}`, {
        headers,
      });

      fetchOrders(); // Refresh the list
    } catch (err) {
      console.error("Delete failed", err);
      alert("Failed to delete order.");
    }
  };

  return (
    <Container className="mt-4">
      <h2 className="mb-4">My Orders</h2>

      <Row className="mb-3 g-2">
        <Col md={3}>
          <Form.Control
            type="date"
            value={filters.startDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, startDate: e.target.value }))
            }
            placeholder="Start date"
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={filters.endDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, endDate: e.target.value }))
            }
            placeholder="End date"
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Filter by bread type"
            value={filters.breadType}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, breadType: e.target.value }))
            }
          />
        </Col>
        <Col md={3}>
          <Form.Select
            size="sm"
            value={ordersPerPage}
            onChange={(e) => {
              const value =
                e.target.value === "all" ? "all" : parseInt(e.target.value);
              setOrdersPerPage(value);
              setCurrentPage(1);
            }}
          >
            <option value="10">Show 10</option>
            <option value="20">Show 20</option>
            <option value="all">Show All</option>
          </Form.Select>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p>Loading orders...</p>
        </div>
      ) : orders.length === 0 ? (
        <p>No orders found.</p>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Order ID</th>
                <th>Order Date</th>
                <th>Items</th>
                <th>Status</th>
                <th>Tracking Code</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, index) => (
                <tr key={order.order_id}>
                  <td>{(currentPage - 1) * ordersPerPage + index + 1}</td>
                  <td>{order.order_id}</td>
                  <td>{new Date(order.order_date).toLocaleDateString()}</td>
                  <td>{summarizeItems(order.items)}</td>
                  <td>{order.order_status}</td>
                  <td>{order.tracking_code || "N/A"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="info"
                      onClick={() => handleView(order.order_id)}
                    >
                      View
                    </Button>{" "}
                    <Button
                      size="sm"
                      variant="warning"
                      onClick={() => handleEdit(order.order_id)}
                    >
                      Edit
                    </Button>{" "}
                    {/* <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDelete(order.order_id)}
                    >
                      Delete
                    </Button> */}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {ordersPerPage !== "all" && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span>
                Showing {(currentPage - 1) * ordersPerPage + 1}–
                {Math.min(currentPage * ordersPerPage, totalOrders)} of{" "}
                {totalOrders}
              </span>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="me-2"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>

                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default OrderList;
